import type { QueryClient } from '@tanstack/react-query';
import {
  invalidateItemsTreeSet,
  reconcileItemsTreeSet,
  type TreeSetPredicate,
} from '@/common/queries/itemsTreeSetConsistency';
import { reconcileAndSettle } from '@/common/queries/itemsTreeSetSettleState';
import { invalidateItemsDataSet } from '@/common/queries/useGetItemsDataSetQuery';
import { invalidateItems } from '@/common/queries/useItems';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { invalidateItemDescendants } from '@/features/items/queries/useItemDescendants';

/**
 * Pass `treeSetReflectsWrite` whenever the caller just changed the item tree (created
 * or reparented an item): the tree-set is then kept refetching until it reflects that
 * write, because the mirror behind it can answer the invalidation's refetch too early.
 * `changedItemIds` is then required too, so the edit still gets its pending/settled highlight.
 */
export function invalidateItemListQueries(queryClient: QueryClient): void;
export function invalidateItemListQueries(
  queryClient: QueryClient,
  treeSetReflectsWrite: TreeSetPredicate,
  changedItemIds: readonly number[],
): void;
export function invalidateItemListQueries(
  queryClient: QueryClient,
  treeSetReflectsWrite?: TreeSetPredicate,
  changedItemIds?: readonly number[],
): void {
  invalidateItems(queryClient);
  invalidateItemsDataSet(queryClient);
  invalidateItemDescendants(queryClient);
  void invalidateItemsTreeSet(queryClient);
  if (treeSetReflectsWrite) {
    void reconcileAndSettle(queryClient, changedItemIds ?? [], {
      isReflected: treeSetReflectsWrite,
    });
  } else {
    void reconcileItemsTreeSet(queryClient);
  }
  queryClient.invalidateQueries({ queryKey: commonQueryKeys.itemAll() });
}
